import AnimatingDots from '~/components/AnimatingDots';
import Divider from '~/components/Divider';
import Gap from '~/components/Gap';
import Text from '~/components/Text';
import { fmPrice } from '~/utils/amount';
import { formatDate } from '~/utils/datetime';
import { BILLING_ROUTE } from '~/constants/routes';
import EstimateContextMenu from '../CellContent/EstimateContextMenu';
import EstimateStatusPills from '../CellContent/EstimateStatusPills';
import EstimateDueStatus from '../EstimateDueStatus';
import { useEstimateDetails } from './useEstimateDetails';
import * as Styles from './styles';
import { useIsDesktop } from '~/hooks/useIsDesktop';
import { useSelector } from '~/store';
import { RootState } from '~/types';

interface IEstimateDetailsProps {
  estimateUuid: string | null;
}

const EstimateDetails = ({ estimateUuid }: IEstimateDetailsProps) => {
  const { isLoading, estimateData } = useEstimateDetails(estimateUuid);
  const isDesktop = useIsDesktop();
  const { currentPlan } = useSelector((state: RootState) => state.user);

  if (isLoading || estimateUuid === null)
    return (
      <Styles.Wrapper className="full-width center">
        <Styles.Section>
          <AnimatingDots />
        </Styles.Section>
      </Styles.Wrapper>
    );

  return (
    <Styles.Wrapper className="full-width">
      <Styles.Section>
        {isDesktop ? (
          <div className="spread align-baseline">
            <div>
              <Text size="xs" variant="neutral" className="semi-bold">
                Estimate total
              </Text>
              <Gap t={8} />
              <Text size="slg" variant="themed" className="bold">
                {fmPrice(estimateData.currency, estimateData.total_amount)}
              </Text>
            </div>
            <div>
              <div className="center">
                <EstimateStatusPills estimateData={estimateData} />
                <Gap l={8} />
                <EstimateContextMenu estimateReferenceId={estimateUuid} />
              </div>
              <Gap t={4} />
              <EstimateDueStatus estimateData={estimateData} />
            </div>
          </div>
        ) : (
          <div className="align-center">
            <div className="full-flex">
              <div className="v-flex-start">
                <div>
                  <Text size="xs" variant="neutral" className="semi-bold">
                    Estimate total
                  </Text>
                  <Gap t={8} />
                  <Text size="md" variant="themed" className="bold">
                    {fmPrice(estimateData.currency, estimateData.total_amount)}
                  </Text>
                  <Gap t={6} />
                </div>
                <div>
                  <EstimateStatusPills estimateData={estimateData} />
                  <Gap t={4} />
                  <EstimateDueStatus estimateData={estimateData} />
                </div>
              </div>
            </div>
            <EstimateContextMenu estimateReferenceId={estimateUuid} />
          </div>
        )}

        <Text size="xs" variant="neutral" className="semi-bold">
          Estimate link
        </Text>
      </Styles.Section>

      <Divider />

      <Styles.Section>
        <div className="d-flex">
          <div className="full-flex">
            <Text size="xs" variant="neutral" className="semi-bold">
              Estimate number
            </Text>
            <Gap t={4} />
            <Text size="sm" className="semi-bold">
              {estimateData.estimate_number}
            </Text>
            <Gap t={20} />
            <Text size="xs" variant="neutral" className="semi-bold">
              Purchase order number
            </Text>
            <Gap t={4} />
            <Text size="sm" className="semi-bold">
              {estimateData.po_number}
            </Text>
          </div>
          <div className="full-flex">
            <Text size="xs" variant="neutral">
              Estimate date
            </Text>
            <Gap t={4} />
            <Text size="sm" className="semi-bold">
              {formatDate(estimateData.estimate_date)}
            </Text>
            <Gap t={20} />
            <Text size="xs" variant="neutral" className="semi-bold">
              Estimate due date
            </Text>
            <Gap t={4} />
            <Text size="sm" className="semi-bold">
              {formatDate(estimateData.due_date)}
            </Text>
          </div>
        </div>
      </Styles.Section>

      <Divider />

      <Styles.Section>
        <Text size="sm" className="bold">
          Client Details
        </Text>
        <Gap t={20} />
        <Text size="sm" className="semi-bold">
          {estimateData.buyer_company_name}
        </Text>
        <Gap t={4} />
        <Text size="xs" variant="secondary">
          {estimateData.buyer_address_line_1}
        </Text>
        <Text size="xs" variant="secondary">
          {estimateData.buyer_address_line_2}
        </Text>
        <Text size="xs" variant="secondary">
          {estimateData.buyer_city}
        </Text>
        <Text size="xs" variant="secondary">
          {estimateData.buyer_state}
        </Text>
        <Text size="xs" variant="secondary">
          {estimateData.buyer_country}
        </Text>
        {estimateData.buyer_email && (
          <Text size="xs" variant="secondary">
            Email: {estimateData.buyer_email}
          </Text>
        )}
      </Styles.Section>

      <Divider />

      <Styles.Section>
        <Text size="sm" className="bold">
          Line items
        </Text>
        <Gap t={20} />

        <Styles.ItemList>
          {estimateData.items.map(eachItem => {
            return (
              <Styles.ItemListItem key={eachItem.id} className="spread align-baseline">
                <div>
                  <Text size="sm" className="semi-bold">
                    {eachItem.description}
                  </Text>
                  <Gap t={2} />
                  <Text size="xs" className="semi-bold" variant="secondary">
                    Qty: {eachItem.quantity}
                  </Text>
                  <Gap t={2} />
                  <Text size="xs" className="semi-bold" variant="secondary">
                    Unit Price: {fmPrice(estimateData.currency, eachItem.price)}
                  </Text>
                </div>

                <div>
                  <Text size="sm" className="bold">
                    {fmPrice(estimateData.currency, eachItem.quantity * eachItem.price)}
                  </Text>
                </div>
              </Styles.ItemListItem>
            );
          })}
        </Styles.ItemList>
      </Styles.Section>

      <Divider />
      <Styles.Section>
        <div className="full-width spread">
          <Text size="sm" className="semi-bold">
            Sub Total
          </Text>
          <Text size="sm" className="semi-bold">
            {fmPrice(estimateData.currency, estimateData.sub_total)}
          </Text>
        </div>
        <Gap t={14} />
        {estimateData.discount_amount ? (
          <>
            <div className="full-width spread">
              <Text variant="secondary" size="sm" className="semi-bold">
                Discount
                {estimateData.discount_percentage ? ` (${estimateData.discount_percentage}%)` : ''}
              </Text>
              <Text variant="secondary" size="sm" className="semi-bold">
                -{fmPrice(estimateData.currency, estimateData.discount_amount)}
              </Text>
            </div>
            <Gap t={14} />
          </>
        ) : null}

        <div className="full-width spread">
          <Text variant="secondary" size="sm" className="semi-bold">
            Tax
            {estimateData.tax_percentage ? ` (${estimateData.tax_percentage}%)` : ''}
          </Text>
          <Text variant="secondary" size="sm" className="semi-bold">
            {fmPrice(estimateData.currency, estimateData.tax_amount)}
          </Text>
        </div>
        <Gap t={14} />
        <div className="full-width spread">
          <Text variant="secondary" size="sm" className="semi-bold">
            Shipping
            {estimateData.shipping_percentage ? ` (${estimateData.shipping_percentage}%)` : ''}
          </Text>
          <Text variant="secondary" size="sm" className="semi-bold">
            {fmPrice(estimateData.currency, estimateData.shipping_amount)}
          </Text>
        </div>
        <Gap t={14} />
      </Styles.Section>

      <Divider />
      <Styles.Section>
        <div className="full-width spread">
          <Text size="sm" className="bold">
            Total Amount
          </Text>
          <Text size="sm" className="bold">
            {fmPrice(estimateData.currency, estimateData.total_amount)}
          </Text>
        </div>
      </Styles.Section>

      {estimateData.pdf_url && (
        <>
          <Divider />
          <Styles.Section>
            <Text size="sm" className="bold">
              Estimate Preview
            </Text>
            <Gap t={20} />
            <Styles.PdfPreview src={estimateData.pdf_url} title="Estimate PDF Preview" />
            <Gap t={16} />
            {currentPlan === 'free' && (
              <Styles.UpgradeLink to={BILLING_ROUTE}>
                Remove Powered By createmyinvoice.com
              </Styles.UpgradeLink>
            )}
          </Styles.Section>
        </>
      )}
      <Gap t={70} />
    </Styles.Wrapper>
  );
};

export default EstimateDetails;
